<template>
  <div class="bg-white rounded-lg shadow-md overflow-hidden">
    <div class="p-4 border-b border-gray-200 flex justify-between items-center">
      <div>
        <h2 class="text-xl font-semibold">Activity Feed</h2>
        <p class="text-sm text-gray-500">Recent actions by team members</p>
      </div>
    </div>
    
    <div class="p-4">
      <ul class="space-y-4">
        <template v-if="activities.length > 0">
          <!-- Activity items from the activities prop -->
          <li v-for="activity in activities" :key="activity.id" class="flex items-start">
            <div class="flex-shrink-0 mr-3">
              <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-500 font-semibold">
                {{ getInitials(activity.user_name) }}
              </div>
            </div>
            <div>
              <p class="text-sm" v-if="activity.description">
                <span class="font-medium">{{ activity.user_name }}</span>
                <span class="text-gray-500">: {{ activity.description }}</span>
              </p>
              <p class="text-sm" v-else>
                <span class="font-medium">{{ activity.user_name }}</span>
                <span class="text-gray-500"> {{ getActionVerb(activity.action) }} </span>
                <span class="font-medium">{{ activity.document_name }}</span>
              </p>
              <p class="text-xs text-gray-500 mt-1">
                <svg class="inline-block h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {{ formatTimeAgo(activity.done_at) }}
              </p>
            </div>
          </li>
        </template>
        
        <!-- Empty state when no activities are available -->
        <li v-if="activities.length === 0" class="text-center py-6">
          <p class="text-gray-500">No recent activities</p>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { TaskDocProfile } from '@/features/task-management';

// Props
defineProps({
  activities: {
    type: Array as () => TaskDocProfile[],
    required: true
  }
});

// Get initials from name
const getInitials = (name: string): string => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 1);
};

// Map action name to verb phrase
const getActionVerb = (action: string): string => {
  if (!action) return ' ';
  
  const actionMap: Record<string, string> = {
    'COMMENT': ' commented on ',
    'DOWNLOAD': ' downloaded ',
    'UPLOAD': ' uploaded ',
    'APPROVE_REJECT': ' reviewed ',
    'REVIEW': ' reviewed ',
    'REQUEST': ' requested ',
    'ADJUST': ' adjusted ',
    'REVISE': ' revised ',
    'REMIND': ' reminded '
  };
  
  return actionMap[action] || action.toLowerCase();
};

// Format time ago
const formatTimeAgo = (timestamp: string | null | undefined): string => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }
};
</script>